<template>
  <div>
    <div class="d-flex flex-wrap align-items-center justify-content-between">
      <h1 id="headline" class="page-header">Dealers</h1>
      <div class="d-flex align-items-center my-2">
        <div>
          <b-input-group>
            <b-input-group-prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search"/>
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input v-model="dealerFilter" debounce="500" placeholder="Filter" type="text"></b-form-input>
          </b-input-group>
        </div>
        <b-button v-b-modal="'add-dealer'" variant="outline-dark" class="ml-3">
          <font-awesome-icon icon="plus"/>
          <span class="ml-2 d-none d-sm-inline">Add Dealer</span>
        </b-button>
      </div>
    </div>

    <b-table :items="dealers" :fields="fields" small striped :filter="dealerFilter">
      <template #cell(affected_serials_count)="data">
        <span v-if="data.value > 0">
          <a href="#" @click="showAffectedMachineModal(data.item.affected_serials, data.item.name)">
            {{ data.value }} machine{{ data.value > 1 ? 's' : '' }}
          </a>
        </span>
        <span v-else>
          0 machines
        </span>
      </template>
      <template #cell(actions)="data">
        <div class="float-right">
          <b-dropdown right variant="link" size="sm" no-caret class="ml-2">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v"/>
            </template>
            <b-dropdown-item @click="openUpdateDealerModal(data.item)">
              Update Dealer
            </b-dropdown-item>
            <b-dropdown-item @click="openDeleteDealerModal(data.item)">
              Delete Dealer
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <!-- delete dealer Modal -->
    <b-modal id="delete-dealer" ref="delete-dealer" title="Deleting Dealer" @ok="deleteDealer($event)" @hidden="resetModals"
             ok-title="Delete" ok-variant="danger">
      <b-alert v-if="dealerError" show variant="danger">
        {{ dealerError }}
      </b-alert>
      <span>Your about to delete the Dealer '{{ selectedDealer?.name }}', press delete to continue</span>
    </b-modal>
    <!-- add dealer Modal -->
    <b-modal id="add-dealer" ref="add-dealer" title="Adding new dealer" @ok="addDealer($event)" @hidden="resetModals"
             ok-title="Create" ok-variant="success" :ok-disabled="newDealerName.length === 0">
      <b-alert v-if="dealerError" show variant="danger">
        {{ dealerError }}
      </b-alert>
      <b-form-group label="Dealer Name" label-for="dealer-name">
        <b-form-input
            v-model="newDealerName"
            placeholder="Enter dealer name"
            id="dealer-name"
        />
      </b-form-group>
    </b-modal>
    <!-- update dealer Modal -->
    <b-modal id="update-dealer" ref="update-dealer" title="Updating dealer" @ok="updateDealer($event)" @hidden="resetModals"
             ok-title="Update" ok-variant="success" :ok-disabled="newDealerName.length === 0">
      <b-alert v-if="dealerError" show variant="danger">
        {{ dealerError }}
      </b-alert>
      <b-form-group label="Dealer Name" label-for="dealer-name" v-if="selectedDealer">
        <b-form-input
            v-model="selectedDealer.name"
            placeholder="Enter dealer name"
            id="dealer-name"
            :disabled="true"
        />
      </b-form-group>
      <b-form-group label="New Dealer Name" label-for="new-dealer-name">
        <b-form-input
            v-model="newDealerName"
            placeholder="Enter new dealer name"
            id="new-dealer-name"
        />
      </b-form-group>
    </b-modal>
    <dealers-modal-affected-machines ref="dealersModalAffectedMachines"></dealers-modal-affected-machines>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import dealersModalAffectedMachines from '@/components/dealers/DealersModalAffectedMachines.vue'

export default {
  name: 'DealersPage',
  components: { dealersModalAffectedMachines },
  data () {
    return {
      dealerFilter: '',
      selectedDealer: null,
      fields: [
        { 'key': 'name', 'label': 'Name', sortable: true },
        { 'key': 'affected_serials_count', 'label': 'Machines', sortable: true },
        { 'key': 'actions', 'label': 'Actions' },
      ],
      newDealerName: '',
      dealerError: null,
    }
  },
  created () {
    this.$store.dispatch('loadDealers')
  },
  computed: {
    ...mapGetters(['getDealers']),
    dealers () {
      return this.getDealers.map(dealer => {
        return {
          ...dealer,
          affected_serials_count: dealer.affected_serials.length
        }
      })
    },
  },
  methods: {
    showAffectedMachineModal (affectedSerials, dealer) {
      this.$refs.dealersModalAffectedMachines.show(affectedSerials, dealer)
    },
    openUpdateDealerModal (dealer) {
      this.selectedDealer = dealer
      this.newDealerName = dealer.name
      this.$refs['update-dealer'].show()
    },
    openDeleteDealerModal (dealer) {
      this.selectedDealer = dealer
      this.$refs['delete-dealer'].show()
    },
    resetModals () {
      this.selectedDealer = null
      this.newDealerName = ''
      this.dealerError = null
    },
    async addDealer (event) {
      event.preventDefault()
      try {
        const payload = {
          dealerName: this.newDealerName
        }
        await this.$store.dispatch('addDealer', payload)
        this.$refs['add-dealer'].hide()
        this.resetModals()
      } catch (error) {
        this.dealerError = 'Failed adding dealer. ' + error
      }
    },
    async deleteDealer (event) {
      event.preventDefault()
      try {
        const payload = {
          dealer: this.selectedDealer,
        }
        await this.$store.dispatch('deleteDealer', payload)
        this.$refs['delete-dealer'].hide()
        this.resetModals()
      } catch (error) {
        this.dealerError = 'Failed deleting dealer. ' + error
      }
    },
    async updateDealer (event) {
      event.preventDefault()
      try {
        const payload = {
          dealer: this.selectedDealer,
          newDealerName: this.newDealerName,
        }
        await this.$store.dispatch('updateDealer', payload)
        this.$refs['update-dealer'].hide()
        this.resetModals()
      } catch (error) {
        this.dealerError = 'Failed updating dealer. ' + error
      }
    },
  }
}
</script>

<style lang="scss">
</style>
